import { useState, useEffect, useRef, useCallback } from 'react';
import { searchVendors, type VendorListItem } from '../../services/api';

interface VendorSearchDropdownProps {
  tenantId: string;
  selectedVendor: VendorListItem | null;
  onSelect: (vendor: VendorListItem) => void;
}

export function VendorSearchDropdown({
  tenantId,
  selectedVendor,
  onSelect,
}: VendorSearchDropdownProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<VendorListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Search vendors with debounce
  const performSearch = useCallback(
    async (searchQuery: string) => {
      if (!searchQuery.trim()) {
        setResults([]);
        return;
      }

      setIsLoading(true);
      try {
        const vendors = await searchVendors(tenantId, searchQuery, 10);
        setResults(vendors);
      } catch (err) {
        console.error('Vendor search failed:', err);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    },
    [tenantId]
  );

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, performSearch]);

  const handleSelect = (vendor: VendorListItem) => {
    onSelect(vendor);
    setQuery('');
    setResults([]);
  };

  // If vendor is selected, show it as a static display
  if (selectedVendor) {
    return null; // The parent component handles showing the selected vendor
  }

  return (
    <div>
      {/* Search input */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search vendors..."
          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          {isLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400" />
          ) : (
            <svg
              className="w-4 h-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          )}
        </div>
      </div>

      {/* Inline results (not absolute positioned) */}
      {query.trim() && (
        <div className="mt-2">
          {results.length > 0 ? (
            <>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">
                Search Results ({results.length})
              </p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {results.map((vendor) => (
                  <button
                    key={vendor.id}
                    onClick={() => handleSelect(vendor)}
                    className="w-full text-left px-3 py-2 rounded-md border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
                  >
                    <p className="font-medium text-gray-900">{vendor.displayName}</p>
                    {vendor.companyName && vendor.companyName !== vendor.displayName && (
                      <p className="text-xs text-gray-500">{vendor.companyName}</p>
                    )}
                    {vendor.email && <p className="text-xs text-gray-400">{vendor.email}</p>}
                  </button>
                ))}
              </div>
            </>
          ) : !isLoading ? (
            <p className="text-sm text-gray-500 text-center py-4">No vendors found for "{query}"</p>
          ) : null}
        </div>
      )}
    </div>
  );
}
